/**
 * Performance (`/api/admin/v2/hr/perf`) and the caller's own goals and
 * reviews (`/api/admin/v2/workplace/{performance,goals,reviews}`). HR runs
 * cycles; reviews are only ever written by their reviewer, in My workplace.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_GC_TIME } from '@/client/constants';
import type { Goal, PerformanceReview, ReviewCycle, ReviewCycleDetail, WorkplacePerformance } from '@/client/types/hr';
import { HR_BASE, WORKPLACE_BASE, hrKeys, useHrApi } from './people';

const PERF_BASE = `${HR_BASE}/perf`;

export const perfKeys = {
  all: [...hrKeys.all, 'perf'] as const,
  cycles: () => [...perfKeys.all, 'cycles'] as const,
  cycle: (id: string | undefined) => [...perfKeys.all, 'cycle', id] as const,
  reviews: () => [...perfKeys.all, 'reviews'] as const,
  goals: () => [...perfKeys.all, 'goals'] as const,
  mine: () => [...hrKeys.workplace(), 'performance'] as const,
};

const q = { staleTime: 15_000, gcTime: DEFAULT_GC_TIME };

export const useReviewCyclesQuery = (enabled = true) => {
  const api = useHrApi(PERF_BASE);
  return useQuery({ queryKey: perfKeys.cycles(), queryFn: async () => (await api<{ cycles: ReviewCycle[] }>('/cycles')).cycles ?? [], enabled, ...q });
};

export const useReviewCycleQuery = (id: string | undefined) => {
  const api = useHrApi(PERF_BASE);
  return useQuery({
    queryKey: perfKeys.cycle(id),
    queryFn: () => api<ReviewCycleDetail>(`/cycles/${encodeURIComponent(id as string)}`),
    enabled: Boolean(id),
    ...q,
  });
};

export const usePerfReviewsQuery = (enabled = true) => {
  const api = useHrApi(PERF_BASE);
  return useQuery({ queryKey: perfKeys.reviews(), queryFn: async () => (await api<{ reviews: PerformanceReview[] }>('/reviews')).reviews ?? [], enabled, ...q });
};

export const usePerfGoalsQuery = (enabled = true) => {
  const api = useHrApi(PERF_BASE);
  return useQuery({ queryKey: perfKeys.goals(), queryFn: async () => (await api<{ goals: Goal[] }>('/goals')).goals ?? [], enabled, ...q });
};

function useMut<TIn, TOut>(base: string, fn: (api: ReturnType<typeof useHrApi>, input: TIn) => Promise<TOut>) {
  const api = useHrApi(base);
  const qc = useQueryClient();
  return useMutation({ mutationFn: (input: TIn) => fn(api, input), onSuccess: () => void qc.invalidateQueries({ queryKey: hrKeys.all }) });
}

export type CycleInput = { id?: string; name?: string; startDate?: string; endDate?: string; dueDate?: string | null };

export const useUpsertCycleMutation = () =>
  useMut(PERF_BASE, (api, { id, ...body }: CycleInput) =>
    id ? api<ReviewCycle>(`/cycles/${encodeURIComponent(id)}`, 'PATCH', body) : api<ReviewCycle>('/cycles', 'POST', body),
  );

export const useActivateCycleMutation = () =>
  useMut(PERF_BASE, (api, id: string) => api<ReviewCycle>(`/cycles/${encodeURIComponent(id)}/activate`, 'POST'));

export const useCloseCycleMutation = () =>
  useMut(PERF_BASE, (api, id: string) => api<ReviewCycle>(`/cycles/${encodeURIComponent(id)}/close`, 'POST'));

export type GoalInput = Partial<Omit<Goal, 'id'>> & { id?: string };

/** HR door (`perf.manage`) — anyone's goals. */
export const useUpsertGoalMutation = () =>
  useMut(PERF_BASE, (api, { id, ...body }: GoalInput) =>
    id ? api<Goal>(`/goals/${encodeURIComponent(id)}`, 'PATCH', body) : api<Goal>('/goals', 'POST', body),
  );

export const useDeleteGoalMutation = () =>
  useMut(PERF_BASE, (api, id: string) => api<{ id: string }>(`/goals/${encodeURIComponent(id)}`, 'DELETE'));

// My workplace — always the caller.

export const useMyPerformanceQuery = () => {
  const api = useHrApi(WORKPLACE_BASE);
  return useQuery({ queryKey: perfKeys.mine(), queryFn: () => api<WorkplacePerformance>('/performance'), ...q });
};

export const useMyGoalMutation = () =>
  useMut(WORKPLACE_BASE, (api, { id, ...body }: GoalInput) =>
    id ? api<Goal>(`/goals/${encodeURIComponent(id)}`, 'PATCH', body) : api<Goal>('/goals', 'POST', body),
  );

export const useDeleteMyGoalMutation = () =>
  useMut(WORKPLACE_BASE, (api, id: string) => api<{ id: string }>(`/goals/${encodeURIComponent(id)}`, 'DELETE'));

export type ReviewInput = { id: string; rating?: number | null; strengths?: string; improvements?: string; submit?: boolean };

export const useWriteReviewMutation = () =>
  useMut(WORKPLACE_BASE, (api, { id, submit, ...body }: ReviewInput) =>
    submit
      ? api<PerformanceReview>(`/reviews/${encodeURIComponent(id)}/submit`, 'POST', body)
      : api<PerformanceReview>(`/reviews/${encodeURIComponent(id)}`, 'PATCH', body),
  );
